'use client';

import cx from 'classnames';
import { ChangeEventHandler, InputHTMLAttributes, ReactNode, Ref, useEffect, useImperativeHandle, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { isNotNullable } from '@flipster/utils/nullable';
import Num, { inputFormatter } from '@flipster/utils/num';

import styles from '../Input/input.module.scss';
import { SizeType } from '../types/base';

export const DefaultNumberInputMaxLength = 12;

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'value' | 'onChange'> {
  ref?: Ref<HTMLInputElement>;
  size?: SizeType;
  shape?: 'normal' | 'inner';
  prefix?: ReactNode;
  suffix?: string | ReactNode;
  className?: string;
  isFull?: boolean;
  isError?: boolean;
  inputSize?: number;
  value?: string | number;
  onChange?: (value: string) => void;
  currency?: string;
  precision: number;
  max?: string;
  maxLength?: number;
}

export const NumberInput = ({
  ref,
  size = 'large',
  shape = 'normal',
  onChange,
  value,
  max,
  maxLength = DefaultNumberInputMaxLength,
  className,
  currency,
  precision,
  prefix,
  suffix,
  isError,
  inputSize = 5,
  ...restInputProps
}: NumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayValue = useRef<string>('');
  const cursor = useRef<number>(0);

  useImperativeHandle(ref, () => inputRef.current!);

  displayValue.current = inputFormatter(value, { withComma: true, precision });

  const onCustomChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    let nextValue = e.currentTarget.value;
    let selectionStart = e.currentTarget.selectionStart ?? 0;

    if (!nextValue) {
      onChange?.(nextValue);
      return;
    }

    // https://prextech.slack.com/archives/C07PP3JK7QC/p1739773999043329
    // https://www.figma.com/design/gwZidquUjZcCTDrfXJbqkN/Key-screens?node-id=1354-50479&t=AGuwgCQzju5ipnzl-1
    // 마침표 콤마 포함한 maxLength 초과 여부 확인
    const nextFormatted = inputFormatter(nextValue, { withComma: true, precision });
    const currentIsOverMaxLength = maxLength && displayValue.current.length > maxLength;
    // maxLength 초과한 값에서 backspace 시 한 번에 전부 지움 (앱과 싱크 맞춤)
    const isDeletingFromOverMax = currentIsOverMaxLength && nextFormatted.length < displayValue.current.length;
    if (isDeletingFromOverMax) {
      onChange?.('');
      return;
    }
    const isOverMaxLength = maxLength && nextFormatted.length > maxLength;
    if (Num(nextValue).isNaN() || isOverMaxLength) {
      e.preventDefault();
      return;
    }
    // 12자째가 "."이면 소수 자릿수를 추가할 수 없는 dead-end 상태가 되므로 거부 (앱과 싱크 맞춤)
    if (maxLength && nextFormatted.length === maxLength && nextFormatted.endsWith('.')) {
      e.preventDefault();
      return;
    }

    const isOverMax = !!max && Num(nextValue).isGreaterThan(max);

    if (isOverMax) {
      cursor.current = -1; // 제일 뒤로 보내기
      onChange?.(inputFormatter(max, { precision, withComma: false }));
      return;
    }

    let formatValue: string = inputFormatter(nextValue, { withComma: true });
    // Delete comma 이전 display value === format value
    if (displayValue.current === formatValue) {
      // 앞자리 문자 삭제
      nextValue = nextValue.substring(0, selectionStart - 1) + nextValue.substring(selectionStart, nextValue.length);
      formatValue = inputFormatter(nextValue, { withComma: true });
      selectionStart -= 1; // 앞자리 문자 삭제기 때문에 하나만큼 더 앞으로
    }
    // next cursor position
    cursor.current = selectionStart + formatValue.length - nextValue.length;
    onChange?.(inputFormatter(nextValue, { precision, withComma: false }));
  };

  useEffect(() => {
    if (!inputRef.current) return;
    const isFocus = document.activeElement === inputRef.current;
    if (isFocus && isNotNullable(cursor.current)) {
      inputRef.current.setSelectionRange(cursor.current, cursor.current);
    }
  }, [value]);

  return (
    <div
      className={cx(styles.wrapper, styles.number, styles[shape], className, {
        [styles[size]]: shape !== 'inner',
        [styles.isError]: isError,
      })}
    >
      {prefix}
      <input
        type='text'
        value={displayValue.current}
        onChange={onCustomChange}
        maxLength={maxLength}
        autoCapitalize='off'
        autoComplete='off'
        autoCorrect='off'
        spellCheck='false'
        data-lpignore='true'
        size={inputSize}
        ref={inputRef}
        {...restInputProps}
      />
      {currency && <span className={styles.currency}>{currency}</span>}
      {suffix}
    </div>
  );
};

interface ControlProps extends Omit<NumberInputProps, 'value'> {
  name: string;
}

export const ControlNumberInput = ({ name, onChange: onChangeCallback, ...restProps }: ControlProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({ name, control });

  return (
    <NumberInput
      name={name}
      value={value}
      onChange={(newValue) => {
        onChange(newValue);
        onChangeCallback?.(newValue);
      }}
      onBlur={onBlur}
      ref={ref}
      {...restProps}
    />
  );
};
